'use client';

import { components } from '@/slices';
import { SliceZone } from '@prismicio/react';
import { useRouter } from 'next/navigation';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { GetStartedDocument } from '../../../prismicio-types';
import { ContentRelationshipField } from '@prismicio/client';
import { useMultiFormStore } from '@/store';
import { useEffect, useRef } from 'react';
import { registerContact } from '@/actions';
import cookies from 'js-cookie';
import { toast } from 'react-toastify';

const FIRST_STEP = 1;

type GetStartedField = ContentRelationshipField<'get_started'> &
  GetStartedDocument;

interface MultiStepFormProps {
  document: GetStartedDocument;
  hubspotPortalIdForm: string;
}

enum CONTENT_STRUCTURE {
  singleColumn = 'single-column',
  twoColumn = 'two-column',
}

export const MultiStepForm = ({
  document,
  hubspotPortalIdForm,
}: MultiStepFormProps) => {
  const router = useRouter();

  const {
    form_title: formTitle,
    step_number: stepNumber,
    form_description: formDescription,
    total_steps: totalSteps,
    slices,
    content_structure: contentStructure,
    next_step: nextStep,
    previous_step: previousStep,
    hubspot_form_id: hubspotFormId,
  } = document.data;

  const typedNextStep = nextStep as GetStartedField;
  const typedPreviousStep = previousStep as GetStartedField;

  const { setFormData, formData, clearForm } = useMultiFormStore();

  const {
    register,
    watch,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm({ mode: 'onBlur', defaultValues: formData });

  const hasRedirected = useRef(false);

  useEffect(() => {
    if (
      Object.keys(formData).length === 0 &&
      typedPreviousStep?.uid &&
      !hasRedirected.current
    ) {
      router.push(typedPreviousStep.url!);
      hasRedirected.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLastStep = stepNumber === totalSteps;

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    Object.keys(data).forEach((key) => {
      if (!data[key]) {
        delete data[key];
      }
    });

    if (data.selectedTechnologies) {
      data.roles = [
        ...data.selectedTechnologies,
        ...(data.roles ? [data.roles] : []),
      ].join(', ');
      delete data.selectedTechnologies;
    }

    if (data.additional) {
      data.scope = `${data.scope}, ${data.additional}`;
      delete data.additional;
    }

    try {
      await registerContact({
        properties: data,
        pageUri: `https://devsu.com${window.location.pathname}`,
        pageName: window.document.title,
        hubspotPortalIdForm,
        hubspotGuidForm: hubspotFormId as string,
        hubspotutkCookie: cookies.get('hubspotutk'),
      });
      router.push(typedNextStep.url!);
      clearForm();
    } catch {
      toast.error('Something went wrong!');
    }
  };

  const handleContinue: SubmitHandler<FieldValues> = (data) => {
    setFormData(data);
    router.push(typedNextStep.url!);
  };

  const handleBack = () => {
    router.back();
    if (stepNumber === FIRST_STEP) {
      clearForm();
    }
  };

  const NavigationButtons = () => {
    return (
      <div className="flex gap-x-6">
        <button
          type="button"
          onClick={handleBack}
          className="button-outline-green"
        >
          Back
        </button>
        <button
          className={`button-filled-green transition-opacity ${
            Object.keys(errors).length > 0 || isSubmitting
              ? 'opacity-50 cursor-not-allowed'
              : ''
          }`}
          type="button"
          onClick={handleSubmit(isLastStep ? onSubmit : handleContinue)}
          disabled={Object.keys(errors).length > 0 || isSubmitting}
          aria-label="Continue or Submit form"
        >
          {isLastStep ? 'Submit' : 'Continue'}
        </button>
      </div>
    );
  };

  const Information = () => {
    return (
      <>
        <p className="text-regular-semibold">
          Step {stepNumber}/{totalSteps}
        </p>
        <h1 className="text-h2">{formTitle}</h1>
        <p className="text-medium-normal">{formDescription}</p>
      </>
    );
  };

  const OneColumnLayout = (): JSX.Element => {
    return (
      <div className="flex flex-col items-center gap-y-12">
        <div className="flex flex-col gap-y-4 text-center">
          <Information />
        </div>
        <div className="w-full flex flex-col gap-y-12">
          <SliceZone
            slices={slices}
            components={components}
            context={{ register, errors, watch }}
          />
        </div>
        <NavigationButtons />
      </div>
    );
  };

  const TwoColumnLayout = (): JSX.Element => {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-20">
        <div className="flex flex-col gap-y-8 text-left">
          <Information />
          <SliceZone
            slices={slices.filter((slice) => slice.slice_type === 'inputs')}
            components={components}
            context={{ register, errors, watch }}
          />
          <NavigationButtons />
        </div>
        <SliceZone
          slices={slices.filter((slice) => slice.slice_type !== 'inputs')}
          components={components}
          context={{ register, errors, watch }}
        />
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {contentStructure === CONTENT_STRUCTURE.singleColumn && (
        <OneColumnLayout />
      )}
      {contentStructure === CONTENT_STRUCTURE.twoColumn && <TwoColumnLayout />}
    </form>
  );
};
